import React, { useMemo, useState } from 'react';
import {
  Truck,
  PlusCircle,
  FileDown,
  CheckCircle2,
  Clock4,
  Package,
  AlertTriangle
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import DataTable from './DataTable';
import Badge from './common/Badge';
import Button from './common/Button';
import StatCard from './StatCard';
import { useAppContext } from '../context/AppContext';
import { formatDate } from '../utils/helpers';
import EmptyState from './common/EmptyState';
import { t } from '../i18n';

const emptyDispatchItem = () => ({
  sku: '',
  description: '',
  unit: 'unidad',
  quantity: 1
});

const Shipments = () => {
  const {
    shipments,
    locations,
    inventory,
    currentUser,
    parks,
    createShipment,
    markShipmentReceived,
    findAreaById,
    findParkById,
    findCostCenterById
  } = useAppContext();

  if (!parks || parks.length === 0) {
    return (
      <EmptyState
        title={t('Envíos a terreno: faltan proyectos')}
        description={t(
          'Para despachar materiales necesitas un proyecto (para imputar CECO y trazabilidad del movimiento).'
        )}
        checklist={['Crear proyecto en Desarrollo o Administración y configurar Área + CECO.']}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
        secondaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
      />
    );
  }

  if (!locations || locations.length < 2) {
    return (
      <EmptyState
        title={t('Envíos a terreno: faltan ubicaciones')}
        description={t(
          'Para crear un despacho necesitas al menos 2 ubicaciones (origen y destino).'
        )}
        checklist={['Crear bodega central + ubicación de proyecto (o cargar templates).']}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  if (!inventory || inventory.length === 0) {
    return (
      <EmptyState
        title={t('Envíos a terreno: no hay stock disponible')}
        description={t(
          'El módulo de despachos valida stock por SKU. Si no hay inventario, no podrás despachar.'
        )}
        checklist={[
          'Cargar inventario por CSV o recepcionar OCs (líneas con SKU).',
          'Confirmar stock en la ubicación de origen.'
        ]}
        primaryAction={{ label: 'Ir a Inventario', pageId: 'procurement-inventory' }}
        secondaryAction={{ label: 'Ir a Órdenes de compra', pageId: 'procurement-orders' }}
      />
    );
  }

  const defaultPark = parks?.[0];
  const defaultArea = defaultPark?.areas?.[0];

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [formState, setFormState] = useState({
    originLocationId: locations[0]?.id || '',
    destinationLocationId: locations[1]?.id || '',
    parkId: defaultPark?.id || '',
    areaId: defaultArea?.id || '',
    costCenterId: defaultArea?.costCenterId || '',
    responsible: '',
    expectedDelivery: '',
    items: [emptyDispatchItem()],
    notes: ''
  });
  const [formError, setFormError] = useState('');

  const dispatchColumns = [
    { key: 'reference', label: t('Guía'), sortable: true },
    {
      key: 'originLocationId',
      label: t('Origen'),
      render: (locationId) => locations.find((loc) => loc.id === locationId)?.name || '-'
    },
    {
      key: 'destinationLocationId',
      label: t('Destino'),
      render: (locationId) => locations.find((loc) => loc.id === locationId)?.name || '-'
    },
    {
      key: 'parkId',
      label: t('Parque'),
      render: (parkId) => findParkById(parkId)?.name || '-'
    },
    {
      key: 'areaId',
      label: t('Área'),
      render: (areaId) => findAreaById(areaId)?.name || '-'
    },
    {
      key: 'costCenterId',
      label: t('Centro Costo'),
      render: (id) => findCostCenterById(id)?.code || '-'
    },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (status) => (
        <Badge tone={status === 'recepcionado' ? 'success' : 'info'}>{t(status)}</Badge>
      )
    },
    {
      key: 'expectedDelivery',
      label: t('Entrega'),
      sortable: true,
      render: (date) => (date ? formatDate(date) : 'Por confirmar')
    },
    {
      key: 'items',
      label: t('Ítems'),
      render: (items) =>
        `${items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)} unidades`
    }
  ];

  const shipmentsInTransit = shipments.filter((shipment) => shipment.status !== 'recepcionado').length;

  const availableInventoryByLocation = useMemo(() => {
    const map = {};
    locations.forEach((location) => {
      map[location.id] = inventory.map((item) => {
        const stock = item.locations.find((loc) => loc.locationId === location.id);
        return {
          sku: item.sku,
          description: item.description,
          unit: item.unit,
          quantity: stock ? stock.quantity : 0
        };
      });
    });
    return map;
  }, [inventory, locations]);

  const parkAreas = useMemo(() => {
    const park = formState.parkId ? findParkById(formState.parkId) : defaultPark;
    return park?.areas || [];
  }, [formState.parkId, findParkById, defaultPark]);

  const currentCostCenter = formState.costCenterId ? findCostCenterById(formState.costCenterId) : null;
  const selectedShipmentArea = selectedShipment ? findAreaById(selectedShipment.areaId) : null;
  const selectedShipmentPark = selectedShipment
    ? selectedShipment.parkId
      ? findParkById(selectedShipment.parkId)
      : selectedShipmentArea
        ? findParkById(selectedShipmentArea.parkId)
        : null
    : null;
  const selectedShipmentCostCenter = selectedShipment?.costCenterId
    ? findCostCenterById(selectedShipment.costCenterId)
    : null;

  const handleAddItemRow = () => {
    setFormState((prev) => ({ ...prev, items: [...prev.items, emptyDispatchItem()] }));
  };

  const handleItemChange = (index, key, value) => {
    setFormState((prev) => {
      const items = prev.items.map((item, idx) =>
        index === idx ? { ...item, [key]: key === 'quantity' ? Number(value) : value } : item
      );

      if (key === 'sku') {
        const inventoryItem = inventory.find((item) => item.sku === value);
        if (inventoryItem) {
          items[index].description = inventoryItem.description;
          items[index].unit = inventoryItem.unit;
        }
      }

      return { ...prev, items };
    });
  };

  const handleRemoveItemRow = (indexToRemove) => {
    setFormState((prev) => ({
      ...prev,
      items: prev.items.filter((_, index) => index !== indexToRemove)
    }));
  };

  const validateStock = () => {
    const availableStock = availableInventoryByLocation[formState.originLocationId] || [];
    for (const item of formState.items) {
      if (!item.sku) return 'Debe seleccionar el SKU de cada ítem.';
      const stockAtOrigin = availableStock.find((stock) => stock.sku === item.sku);
      if (!stockAtOrigin || stockAtOrigin.quantity < item.quantity) {
        return `Stock insuficiente para ${item.sku} en la ubicación de origen.`;
      }
    }
    if (formState.originLocationId === formState.destinationLocationId) {
      return 'El origen y destino deben ser distintos.';
    }
    return '';
  };

  const handleCreateShipment = (event) => {
    event.preventDefault();
    const validationMessage = validateStock();
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }
    setFormError('');

    createShipment({
      ...formState,
      items: formState.items.map((item) => ({
        sku: item.sku,
        description: item.description,
        quantity: Number(item.quantity),
        unit: item.unit
      }))
    });
    setShowCreateModal(false);
    setFormState({
      originLocationId: locations[0]?.id || '',
      destinationLocationId: locations[1]?.id || '',
      parkId: defaultPark?.id || '',
      areaId: defaultArea?.id || '',
      costCenterId: defaultArea?.costCenterId || '',
      responsible: '',
      expectedDelivery: '',
      items: [emptyDispatchItem()],
      notes: ''
    });
  };

  const generateDispatchGuide = (shipment) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Guía de Despacho - Fenic Energy Platform', 14, 20);

    doc.setFontSize(12);
    doc.text(`Referencia: ${shipment.reference}`, 14, 34);
    doc.text(`Fecha emisión: ${formatDate(shipment.createdAt)}`, 14, 42);
    doc.text(`Origen: ${locations.find((loc) => loc.id === shipment.originLocationId)?.name || '-'}`, 14, 50);
    doc.text(`Destino: ${locations.find((loc) => loc.id === shipment.destinationLocationId)?.name || '-'}`, 14, 58);
    const park = shipment.parkId ? findParkById(shipment.parkId) : null;
    const area = shipment.areaId ? findAreaById(shipment.areaId) : null;
    const costCenter = shipment.costCenterId ? findCostCenterById(shipment.costCenterId) : null;
    if (park) {
      doc.text(`Parque: ${park.name}`, 14, 66);
    }
    if (area) {
      doc.text(`Área: ${area.name}`, 14, park ? 74 : 66);
    }
    if (costCenter) {
      const y = park || area ? (park && area ? 82 : 74) : 66;
      doc.text(`Centro costo: ${costCenter.code}`, 14, y);
    }
    const baseY = park || area || costCenter ? 90 : 66;
    doc.text(`Responsable: ${shipment.responsible || 'No asignado'}`, 14, baseY);
    if (shipment.expectedDelivery) {
      doc.text(`Entrega estimada: ${formatDate(shipment.expectedDelivery)}`, 14, baseY + 8);
    }

    const itemsStartY = shipment.expectedDelivery ? baseY + 16 : baseY + 8;
    doc.text('Detalle de ítems:', 14, itemsStartY);
    let positionY = itemsStartY + 8;
    shipment.items.forEach((item, index) => {
      doc.text(
        `${index + 1}. ${item.description} (SKU ${item.sku}) - ${item.quantity} ${item.unit}`,
        14,
        positionY
      );
      positionY += 8;
    });

    doc.text('Observaciones:', 14, positionY + 6);
    doc.text(shipment.notes || 'Sin observaciones', 14, positionY + 14);

    doc.save(`Guia-${shipment.reference}.pdf`);
  };

  const totalUnitsDispatched = shipments.reduce(
    (sum, shipment) =>
      sum + shipment.items.reduce((itemSum, item) => itemSum + Number(item.quantity || 0), 0),
    0
  );

  const totalGuides = shipments.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Envíos a Terreno')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Crea despachos entre bodegas y proyectos, registra recepción conforme y genera guías en PDF.'
          )}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500">
            <p className="font-semibold text-slate-600">{t('Despachos activos:')} {t(shipmentsInTransit)}</p>
            <p>{t('Total guías emitidas:')} {t(totalGuides)}</p>
          </div>
          {['comprador', 'project_manager', 'purchasing_manager', 'super_user'].includes(currentUser.role) && (
            <Button variant="primary" icon={PlusCircle} onClick={() => setShowCreateModal(true)}>{t('Nuevo despacho')}</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title={t('Despachos activos')} value={shipmentsInTransit} icon={Truck} />
        <StatCard title={t('Unidades movilizadas')} value={totalUnitsDispatched} icon={Package} />
        <StatCard title={t('Guías emitidas')} value={totalGuides} icon={Clock4} />
      </div>

      <DataTable
        data={shipments}
        columns={[...dispatchColumns, {
          key: 'actions',
          label: 'Acciones',
          render: (_, shipment) => (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setSelectedShipment(shipment)}>
                Ver detalle
              </Button>
              <Button variant="secondary" size="sm" onClick={() => generateDispatchGuide(shipment)}>
                Guía PDF
              </Button>
            </div>
          )
        }]}
        searchPlaceholder={t('Buscar por guía, origen, destino...')}
        onRowClick={setSelectedShipment}
      />

      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-6">
          <form
            className="bg-white rounded-3xl border border-slate-100 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 space-y-6"
            onSubmit={handleCreateShipment}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{t('Nuevo despacho a terreno')}</h2>
                  <p className="text-sm text-slate-500">{t('Selecciona origen, destino y define los materiales a enviar.')}</p>
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-500 hover:text-slate-900"
                  onClick={() => setShowCreateModal(false)}
                >{t('cerrar')}</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Origen')}</label>
                  <select
                    value={formState.originLocationId}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, originLocationId: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    required
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {t(location.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Destino')}</label>
                  <select
                    value={formState.destinationLocationId}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, destinationLocationId: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    required
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {t(location.name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Parque')}</label>
                  <select
                    value={formState.parkId}
                    onChange={(event) => {
                      const nextParkId = event.target.value;
                      const park = findParkById(nextParkId) || parks.find((p) => p.id === nextParkId);
                      const firstArea = park?.areas?.[0];
                      setFormState((prev) => ({
                        ...prev,
                        parkId: nextParkId,
                        areaId: firstArea?.id || '',
                        costCenterId: firstArea?.costCenterId || ''
                      }));
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    required
                  >
                    {parks.map((park) => (
                      <option key={park.id} value={park.id}>
                        {t(park.name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Área')}</label>
                  <select
                    value={formState.areaId}
                    onChange={(event) => {
                      const nextAreaId = event.target.value;
                      const area = findAreaById(nextAreaId);
                      setFormState((prev) => ({
                        ...prev,
                        areaId: nextAreaId,
                        costCenterId: area?.costCenterId || prev.costCenterId
                      }));
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    required
                  >
                    {parkAreas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {t(area.name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Centro de costo')}</label>
                  <input
                    type="text"
                    value={currentCostCenter ? `${currentCostCenter.code} · ${currentCostCenter.name}` : 'Sin asignar'}
                    readOnly
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Responsable en terreno')}</label>
                  <input
                    value={formState.responsible}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, responsible: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    placeholder={t('Nombre y apellido')}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Fecha entrega estimada')}</label>
                  <input
                    type="date"
                    value={formState.expectedDelivery}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, expectedDelivery: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">{t('Materiales a despachar')}</h4>
                  <button
                    type="button"
                    onClick={handleAddItemRow}
                    className="text-sm font-semibold text-brand-600 hover:text-brand-700"
                  >{t('+ Añadir ítem')}</button>
                </div>

                <div className="space-y-3">
                  {formState.items.map((item, index) => {
                    const availableStock =
                      availableInventoryByLocation[formState.originLocationId]?.find(
                        (stock) => stock.sku === item.sku
                      )?.quantity || 0;
                    return (
                      <div
                        key={`dispatch-item-${index}`}
                        className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] gap-4 bg-slate-50 border border-slate-100 rounded-2xl p-4"
                      >
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('SKU')}</label>
                          <select
                            value={item.sku}
                            onChange={(event) => handleItemChange(index, 'sku', event.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                            required
                          >
                            <option value="">{t('Selecciona material...')}</option>
                            {inventory.map((inventoryItem) => (
                              <option key={inventoryItem.id} value={inventoryItem.sku}>
                                {t(inventoryItem.sku)} · {t(inventoryItem.description)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Descripción')}</label>
                          <input
                            value={item.description}
                            onChange={(event) =>
                              handleItemChange(index, 'description', event.target.value)
                            }
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                            placeholder={t('Descripción del ítem')}
                            required
                          />
                          <p className="text-xs text-slate-400">{t('Disponible en origen:')} {t(availableStock)} {t(item.unit)}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Cantidad')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(event) => handleItemChange(index, 'quantity', event.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                            required
                          />
                          {formState.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItemRow(index)}
                              className="text-xs text-danger-600 hover:text-danger-700 font-semibold"
                            >{t('Eliminar')}</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-600">{t('Observaciones')}</label>
                <textarea
                  value={formState.notes}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                  rows={3}
                  placeholder={t('Instrucciones especiales, requisitos de recepción, etc.')}
                />
              </div>

              {formError && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-danger-100 bg-danger-50 text-sm text-danger-600">
                  <AlertTriangle className="w-4 h-4" />
                  {t(formError)}
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>{t('Cancelar')}</Button>
                <Button variant="primary" type="submit">{t('Crear despacho')}</Button>
              </div>
            </form>
        </div>
      )}

      {selectedShipment && (
        <div className="fixed inset-0 bg-slate-900/50 flex justify-end z-40">
            <div className="w-full max-w-xl h-full bg-white border-l border-slate-100 shadow-2xl p-8 overflow-y-auto space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase">{t('Detalle despacho')}</p>
                  <h3 className="text-xl font-bold text-slate-900">{t(selectedShipment.reference)}</h3>
                  <p className="text-xs text-slate-500">
                    {formatDate(selectedShipment.createdAt)} {t('· Estado:')} {t(selectedShipment.status)}
                  </p>
                </div>
                <button
                  className="text-sm text-slate-500 hover:text-slate-900"
                  onClick={() => setSelectedShipment(null)}
                >{t('cerrar')}</button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Origen')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {t(
                      locations.find((loc) => loc.id === selectedShipment.originLocationId)?.name
                    )}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Destino')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {t(
                      locations.find((loc) => loc.id === selectedShipment.destinationLocationId)?.name
                    )}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Parque / Área')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {t(selectedShipmentPark?.name) || t('Sin asignar')}
                  </p>
                  <p className="text-xs text-slate-400">{t(selectedShipmentArea?.name) || '-'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Centro de costo')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {selectedShipmentCostCenter ? t(selectedShipmentCostCenter.code) : '-'}
                  </p>
                  <p className="text-xs text-slate-400">{t(selectedShipmentCostCenter?.name) || ''}</p>
                </div>
              </div>

              <div className="bg-white border border-slate-100 rounded-xl">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{t('Ítems despachados')}</p>
                  <span className="text-xs text-slate-400">
                    {t(selectedShipment.items.length)} {t('registros')}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {selectedShipment.items.map((item, index) => (
                    <div key={`${selectedShipment.id}-${index}`} className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-800">{t(item.description)}</p>
                          <p className="text-xs text-slate-400">{t('SKU')} {t(item.sku)}</p>
                        </div>
                        <span className="font-semibold text-slate-800">
                          {t(item.quantity)} {t(item.unit)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedShipment.notes && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase mb-2">{t('Observaciones')}</p>
                  <p className="text-sm text-slate-600">{t(selectedShipment.notes)}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  icon={FileDown}
                  onClick={() => generateDispatchGuide(selectedShipment)}
                >{t('Descargar Guía')}</Button>
                {selectedShipment.status !== 'recepcionado' && (
                  <Button
                    variant="primary"
                    icon={CheckCircle2}
                    onClick={() => markShipmentReceived(selectedShipment.id)}
                  >{t('Marcar recepción conforme')}</Button>
                )}
              </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default Shipments;
